import React from 'react';
import { FmText } from '../FmComposition';
import { FmSelector } from '../FmSelector';
import { FmTable, type FmTableColumn } from '../FmTable';
import { useNarrowContainer } from '../FmTable.hooks';
import { FmValueList } from '../FmValueList';
import {
  FM_COMPARISON_MAX_ITEMS,
  type FmStoryComparisonCriterion,
  type FmStoryComparisonItem,
  type FmStoryComparisonMatrix,
} from './elementFields.types';
import './FmElements.css';
import './FmComparisonElement.css';

function useControllable(
  controlled: string | undefined,
  initial: string,
  onChange?: (id: string) => void,
) {
  const [local, setLocal] = React.useState(controlled ?? initial);
  const value = onChange && controlled !== undefined ? controlled : local;
  const change = (next: string): void => {
    if (!onChange || controlled === undefined) setLocal(next);
    onChange?.(next);
  };
  return [value, change] as const;
}

function criterionColumns(
  items: readonly FmStoryComparisonItem[],
  criterionLabel: string,
  drilledId: string,
): FmTableColumn<FmStoryComparisonCriterion>[] {
  return [
    {
      key: 'criterion',
      header: criterionLabel,
      label: criterionLabel,
      role: 'label',
      render: (criterion) => (
        <span className="fm-element-comparison__criterion">
          <strong>{criterion.label}</strong>
          {criterion.detail ? <small>{criterion.detail}</small> : null}
        </span>
      ),
    },
    ...items.map((item) => ({
      key: item.id,
      header: item.label,
      label: item.label,
      className: item.id === drilledId ? 'fm-element-comparison__drilled' : undefined,
      render: (criterion: FmStoryComparisonCriterion) => {
        const value = criterion.values[item.id];
        return (
          <span
            className="fm-element-comparison__value"
            data-winner={criterion.winnerItemId === item.id ? 'true' : undefined}
          >
            {value}
          </span>
        );
      },
    })),
  ];
}

const DrilledFacts: React.FC<{ item: FmStoryComparisonItem }> = ({ item }) => (
  <>
    {item.summary ? <p>{item.summary}</p> : null}
    {item.metrics?.length ? (
      <FmValueList
        density="compact"
        items={item.metrics.map((metric) => ({
          id: metric.label,
          label: metric.label,
          value:
            metric.value === null
              ? metric.unavailableReason
              : [metric.value, metric.unit].filter(Boolean).join('\u00a0'),
          unavailable: metric.value === null,
          helperText: metric.detail,
        }))}
      />
    ) : null}
  </>
);

const DrilledDetail: React.FC<{ item: FmStoryComparisonItem; testId: string }> = ({
  item,
  testId,
}) => (
  <div className="fm-element-comparison__detail" data-testid={testId}>
    <strong>{item.label}</strong>
    {item.status ? (
      <FmText size="caption" tone="muted">
        {item.status}
      </FmText>
    ) : null}
    <DrilledFacts item={item} />
  </div>
);

/**
 * The phone reading of the matrix: one card per item, its name once as the
 * heading and the criteria as value rows, so a long item name never repeats
 * under every criterion. The drilled item's card takes the accent surface and
 * carries its detail, so the selection reads in one place.
 */
const ItemCards: React.FC<{
  items: readonly FmStoryComparisonItem[];
  criteria: readonly FmStoryComparisonCriterion[];
  label: string;
  drilledId: string;
  base: string;
}> = ({ items, criteria, label, drilledId, base }) => (
  <ul className="fm-element-comparison__cards" aria-label={label} data-testid={`${base}-cards`}>
    {items.map((item) => {
      const drilled = item.id === drilledId;
      return (
        <li
          key={item.id}
          className="fm-element-comparison__card"
          aria-current={drilled ? 'true' : undefined}
          data-testid={`${base}-card-${item.id}`}
        >
          <strong className="fm-element-comparison__card-title">{item.label}</strong>
          {item.status ? (
            <FmText size="caption" tone="muted">
              {item.status}
            </FmText>
          ) : null}
          <FmValueList
            density="compact"
            items={criteria.map((criterion) => ({
              id: criterion.key,
              label: criterion.label,
              value: (
                <span
                  className="fm-element-comparison__value"
                  data-winner={criterion.winnerItemId === item.id ? 'true' : undefined}
                >
                  {criterion.values[item.id]}
                </span>
              ),
              helperText: criterion.detail,
            }))}
          />
          {drilled ? (
            <div className="fm-element-comparison__card-detail" data-testid={`${base}-detail`}>
              <DrilledFacts item={item} />
            </div>
          ) : null}
        </li>
      );
    })}
  </ul>
);

function useComparisonSelection(comparison: FmStoryComparisonMatrix) {
  const items = comparison.items.slice(0, FM_COMPARISON_MAX_ITEMS);
  const [groupId, setGroupId] = useControllable(
    comparison.activeGroupId,
    comparison.groups[0]?.id ?? '',
    comparison.onGroupChange,
  );
  const [drilledId, setDrilledId] = useControllable(
    comparison.drilledItemId,
    items[0]?.id ?? '',
    comparison.onDrillItem,
  );
  return {
    items,
    group: comparison.groups.find((entry) => entry.id === groupId) ?? comparison.groups[0],
    drilled: items.find((item) => item.id === drilledId) ?? items[0],
    setGroupId,
    setDrilledId,
  };
}

/**
 * A comparison matrix on the shared table: criteria as rows, the compared items
 * as columns; in a phone-width container it reads item-major as one card per
 * item. The group lens switches criteria sets and the item selector picks
 * the item whose summary and metrics read below the table.
 */
export const FmComparisonView: React.FC<{
  comparison: FmStoryComparisonMatrix;
  testBase: string;
}> = ({ comparison, testBase }) => {
  const { items, group, drilled, setGroupId, setDrilledId } = useComparisonSelection(comparison);
  const { ref, narrow } = useNarrowContainer();
  const base = comparison.testId ?? testBase;
  if (items.length < 2 || !group || !drilled) {
    return (
      <div ref={ref} className="fm-element-unavailable">
        {comparison.emptyLabel}
      </div>
    );
  }
  const criterionLabel = comparison.criterionLabel ?? group.label;
  const groupLabel = comparison.groupLabel ?? criterionLabel;
  return (
    <div ref={ref} className="fm-element-comparison" data-testid={base}>
      {comparison.groups.length > 1 ? (
        <FmSelector
          variant="segmented"
          value={group.id}
          label={groupLabel}
          items={comparison.groups.map((entry) => ({
            value: entry.id,
            label: entry.label,
            'data-testid': `${base}-${entry.id}-lens`,
          }))}
          onChange={setGroupId}
          data-testid={`${base}-lens-selector`}
        />
      ) : null}
      {group.summary ? <p className="fm-element-comparison__summary">{group.summary}</p> : null}
      {narrow ? (
        <ItemCards
          items={items}
          criteria={group.criteria}
          label={group.label}
          drilledId={drilled.id}
          base={base}
        />
      ) : (
        <FmTable<FmStoryComparisonCriterion>
          columns={criterionColumns(items, criterionLabel, drilled.id)}
          rows={group.criteria}
          rowKey={(criterion) => criterion.key}
          density="compact"
          mobileLayout="labelled-cells"
          emptyText={comparison.emptyLabel}
          data-testid={`${base}-table`}
        />
      )}
      <FmSelector
        variant="chips"
        value={drilled.id}
        label={groupLabel}
        items={items.map((item) => ({
          value: item.id,
          label: item.label,
          'data-testid': `${item.testId ?? item.id}-drill`,
        }))}
        onChange={setDrilledId}
        data-testid={`${base}-drill`}
      />
      {narrow ? null : <DrilledDetail item={drilled} testId={`${base}-detail`} />}
    </div>
  );
};
